/** biome-ignore-all lint/correctness/useHookAtTopLevel: this hook is used at the top level of the client */
'use client';

import { c as _c } from "react/compiler-runtime";
import type { FlatNamespace, KeyPrefix } from 'i18next';
import { useParams } from 'next/navigation';
import type { ReactElement, ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { type FallbackNs, type UseTranslationOptions, type UseTranslationResponse, useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next/TransWithoutContext';
import i18next from './i18n';
import { defaultLanguage, type LanguageCode } from './i18n-resources/languages';

// TransWithoutContext is typically used for server components but the desktop app uses it for client components too.
// exporting it here allows the desktop app to use it without having to import it from 'react-i18next/TransWithoutContext'.
export { Trans };
type $Tuple<T> = readonly [T?, ...T[]];
const runsOnServerSide = typeof window === 'undefined';

/** Context for the current language when provided by a host (e.g. desktop). SSR-safe: provider is stateless. */
export const CurrentLanguageContext = createContext<LanguageCode | null>(null);
export interface CurrentLanguageProviderProps {
  value: LanguageCode | null;
  children: ReactNode;
}

/** Stateless provider for current language. Use when the host drives language (e.g. Aurelia I18n). SSR-safe. */
export function CurrentLanguageProvider(t0) {
  const $ = _c(3);
  const {
    value,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== value) {
    t1 = <CurrentLanguageContext.Provider value={value}>{children}</CurrentLanguageContext.Provider>;
    $[0] = children;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

// This hook manages the state of the current language for the client. It reads from context first (React SPA),
// then falls back to route params (Next).
export function useT<Ns extends FlatNamespace | $Tuple<FlatNamespace>, KPrefix extends KeyPrefix<FallbackNs<Ns>> = undefined>(ns?: Ns, options?: UseTranslationOptions<KPrefix>): UseTranslationResponse<FallbackNs<Ns>, KPrefix> {
  const contextLng = useContext(CurrentLanguageContext);
  const paramsLng = useParams()?.lang;
  const paramsLngStr = typeof paramsLng === 'string' ? paramsLng : undefined;
  const effectiveLang: string = contextLng ?? paramsLngStr ?? defaultLanguage;
  if (contextLng == null && paramsLng != null && typeof paramsLng !== 'string') {
    throw new Error('Missing CurrentLanguageContext or [lang] route param. [lang] must be a string. useT must have either a CurrentLanguageContext provider or a lang route param from a localized route (under /[lang])');
  }
  const [activeLng, setActiveLng] = useState(i18next.resolvedLanguage);
  if (runsOnServerSide && i18next.resolvedLanguage !== effectiveLang) {
    i18next.changeLanguage(effectiveLang);
  } else {
    useEffect(() => {
      if (activeLng !== i18next.resolvedLanguage) {
        setActiveLng(i18next.resolvedLanguage);
      }
    }, [activeLng]);
    useEffect(() => {
      if (i18next.isInitialized && i18next.resolvedLanguage !== effectiveLang) {
        i18next.changeLanguage(effectiveLang);
      }
    }, [effectiveLang]);
  }
  return useTranslation(ns, options);
}